import {
  AlertDialog,
  Badge,
  BodyText,
  Collapsible,
  toast,
} from "@/components/ui";
import { TRACKERS } from "@/constants/Trackers";
import { formatDate, formatDateTime } from "@/lib/time";
import { trackEvent } from "@/lib/tracking";
import { useConfigCatFeatureFlag } from "@/network/useConfigCatFeatureFlag";
import { isActionAvailable } from "@/utils/available-actions";
import { constants, getCodeValue, getDisplay } from "@curai/js-fhir-tools";
import { useParams } from "@tanstack/react-router";
import {
  Archive,
  Check,
  CircleX,
  ClipboardPlus,
  LoaderCircle,
  MessagesSquare,
  Redo2,
  TriangleAlert,
} from "lucide-react";
import { useState } from "react";
import {
  useActivateCondition,
  useConfirmCondition,
  useConfirmMedicalHistory,
  useReactivateMedicalHistory,
  useResolveCondition,
  useResolveMedicalHistory,
  useUpdateConditionNote,
  useUpdateMedicalHistory,
  useVoidCondition,
  useVoidMedicalHistory,
} from "../network";
import { type MedicalHistory, MedicalHistoryAction } from "../types";
import { MedicalHistoryForm } from "./MedicalHistoryForm";
export const VERIFICATION_STATUS_SYSTEM_URL =
  "http://terminology.hl7.org/CodeSystem/condition-ver-status";

export const MedicalHistoryItem = ({
  medicalHistory,
}: { medicalHistory: MedicalHistory }) => {
  const { patientId } = useParams({ strict: false });
  const [showVoidPrompt, setShowVoidPrompt] = useState(false);
  const { data: isSingleEncounterVisitsEnabled } = useConfigCatFeatureFlag(
    "single-encounter-visits",
  );

  const { mutate: resolveMedicalHistory, isPending: isResolving } =
    useResolveMedicalHistory({
      patientId: patientId as string,
      episodeOfCareId: medicalHistory.episodeOfCare.id as string,
      onError: (error) => {
        toast.error(
          `Failed to resolve medical history: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const { mutate: voidMedicalHistory, isPending: isVoiding } =
    useVoidMedicalHistory({
      patientId: patientId as string,
      episodeOfCareId: medicalHistory.episodeOfCare.id as string,
      onError: () => {
        toast.error("Failed to void medical history.");
      },
    });

  const { mutate: reactivateMedicalHistory, isPending: isReactivating } =
    useReactivateMedicalHistory({
      patientId: patientId as string,
      episodeOfCareId: medicalHistory.episodeOfCare.id as string,
      onError: (error) => {
        toast.error(
          `Failed to reactivate medical history: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const { mutate: confirmMedicalHistory, isPending: isConfirming } =
    useConfirmMedicalHistory({
      patientId: patientId as string,
      episodeOfCareId: medicalHistory.episodeOfCare.id as string,
      onError: (error) => {
        toast.error(
          `Failed to confirm medical history: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const { mutate: updateMedicalHistory, isPending: isUpdating } =
    useUpdateMedicalHistory({
      patientId: patientId as string,
      episodeOfCareId: medicalHistory.episodeOfCare.id as string,
      onError: (error) => {
        toast.error(
          `Failed to update medical history: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const { mutate: updateConditionNote, isPending: isUpdatingConditionNote } =
    useUpdateConditionNote({
      patientId: patientId as string,
      conditionId: medicalHistory.condition.id as string,
      onError: (error) => {
        toast.error(
          `Failed to update medical history note: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const { mutate: resolveCondition, isPending: isResolvingCondition } =
    useResolveCondition({
      patientId: patientId as string,
      conditionId: medicalHistory.condition.id as string,
      onError: (error) => {
        toast.error(
          `Failed to resolve medical history: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const { mutate: voidCondition, isPending: isVoidingCondition } =
    useVoidCondition({
      patientId: patientId as string,
      conditionId: medicalHistory.condition.id as string,
      onError: () => {
        toast.error("Failed to void medical history.");
      },
    });

  const { mutate: activateCondition, isPending: isActivatingCondition } =
    useActivateCondition({
      patientId: patientId as string,
      conditionId: medicalHistory.condition.id as string,
      onError: (error) => {
        toast.error(
          `Failed to reactivate medical history: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const { mutate: confirmCondition, isPending: isConfirmingCondition } =
    useConfirmCondition({
      patientId: patientId as string,
      conditionId: medicalHistory.condition.id as string,
      onError: (error) => {
        toast.error(
          `Failed to confirm medical history: ${error.response?.data?.detail || error.message}`,
        );
      },
    });

  const isActive = medicalHistory.episodeOfCare.status === "active";
  const confirmed =
    isSingleEncounterVisitsEnabled === true
      ? medicalHistory.needsConfirmation !== true
      : medicalHistory.condition.verificationStatus &&
        getCodeValue(
          medicalHistory.condition.verificationStatus,
          VERIFICATION_STATUS_SYSTEM_URL,
        ) === "confirmed";

  const actionMenuItems = [];
  if (!confirmed) {
    actionMenuItems.push({
      label: "Confirm",
      onClick: () => {
        trackEvent(TRACKERS.CONFIRM_SINGLE_MEDICAL_HISTORY_ITEM);
        if (isSingleEncounterVisitsEnabled) {
          confirmCondition();

          return;
        }

        confirmMedicalHistory({});
      },
      icon:
        isConfirming || isConfirmingCondition ? (
          <LoaderCircle className="text-link w-3.5 animate-spin" />
        ) : (
          <Check className="text-link w-3.5" data-testid="confirm-button" />
        ),
    });
  }

  const dropdownMenuItems = [];
  if (isActive) {
    dropdownMenuItems.push({
      label: "Resolve",
      onClick: () => {
        trackEvent(TRACKERS.RESOLVE_CONDITION);
        if (isSingleEncounterVisitsEnabled) {
          resolveCondition();

          return;
        }

        resolveMedicalHistory({});
      },
      icon:
        isResolving || isResolvingCondition ? (
          <LoaderCircle className="w-3.5 animate-spin" />
        ) : (
          <Archive className="w-3.5" data-testid="resolve-button" />
        ),
    });
    dropdownMenuItems.push({
      label: "Void",
      disabled: !isActionAvailable(
        MedicalHistoryAction.VOID,
        medicalHistory.availableActions,
      ),
      onClick: () => {
        trackEvent(TRACKERS.VOID_CONDITION);
        setShowVoidPrompt(true);
      },
      icon:
        isVoiding || isVoidingCondition ? (
          <LoaderCircle className="w-3.5 animate-spin" />
        ) : (
          <CircleX className="w-3.5" data-testid="void-button" />
        ),
    });
  } else {
    actionMenuItems.push({
      label: "Reactivate",
      onClick: () => {
        if (isSingleEncounterVisitsEnabled) {
          activateCondition();

          return;
        }

        reactivateMedicalHistory({});
      },
      icon:
        isReactivating || isActivatingCondition ? (
          <LoaderCircle className="text-link w-3.5 animate-spin" />
        ) : (
          <Redo2 className="text-link w-3.5" data-testid="reactivate-button" />
        ),
    });
  }

  return (
    <>
      <Collapsible
        title={
          (medicalHistory.condition?.code &&
            getDisplay(medicalHistory.condition.code)) ||
          "Unknown"
        }
        onOpenChange={(open) => {
          open
            ? trackEvent(TRACKERS.EXPAND_CONDITION)
            : trackEvent(TRACKERS.COLLAPSE_CONDITION);
        }}
        headerSize="sm"
        actionMenu={actionMenuItems}
        forcedDropdownActions={dropdownMenuItems}
        badgeComponents={
          confirmed
            ? []
            : [
                <Badge key="unconfirmed" variant="warning">
                  <TriangleAlert className="w-3.5 stroke-warning" />
                  <BodyText size="sm" textColor="warning">
                    Unconfirmed
                  </BodyText>
                </Badge>,
              ]
        }
        status={
          medicalHistory.lastVisit
            ? `Last visit ${formatDate(medicalHistory.lastVisit)}`
            : undefined
        }
      >
        <div className="flex flex-col gap-2">
          <MedicalHistoryForm
            patientId={patientId as string}
            medicalHistory={medicalHistory}
            isSingleEncounterVisitsEnabled={
              isSingleEncounterVisitsEnabled === true
            }
            onSubmit={(data) => {
              if (isSingleEncounterVisitsEnabled) {
                updateConditionNote({ note: data.note || null });

                return;
              }

              updateMedicalHistory({
                conditionCode: {
                  coding: [
                    {
                      code: data.conditionCode,
                      display: data.conditionCodeDisplay,
                      system: constants.SNOMED_SYSTEM_URL,
                    },
                  ],
                  text: data.conditionCodeDisplay,
                },
              });
            }}
            isSubmitting={isUpdating || isUpdatingConditionNote}
          />
          <div className="flex flex-row gap-1 items-center">
            <ClipboardPlus size={16} className="stroke-tertiary" />
            <BodyText size="xs" textColor="secondary">
              Added by{" "}
              {medicalHistory.condition?.recorder?.display || "Unknown"} on{" "}
              {formatDateTime(medicalHistory.condition?.recordedDate as string)}
            </BodyText>
          </div>
          {medicalHistory.lastVisit && (
            <div className="flex flex-row gap-1 items-center">
              <MessagesSquare className="w-3.5 h-3.5 stroke-secondary" />
              <BodyText size="xs" textColor="secondary">
                Last visit {formatDate(medicalHistory.lastVisit)}
              </BodyText>
            </div>
          )}
        </div>
      </Collapsible>
      <AlertDialog
        isOpen={showVoidPrompt}
        onOpenChange={setShowVoidPrompt}
        isControlled
        title="Delete Condition"
        body={`Are you sure you want to delete this medical history condition? This will remove it entirely from the patient's medical record, and should only be used if was added in error (i.e. the wrong patient).`}
        actionLabel="Void"
        cancelLabel="Cancel"
        onAction={() => {
          setShowVoidPrompt(false);
          if (isSingleEncounterVisitsEnabled) {
            voidCondition();

            return;
          }

          voidMedicalHistory();
        }}
        onCancel={() => {
          setShowVoidPrompt(false);
        }}
        // TODO: Remove this once we fix the AlertDialogProvider
        // biome-ignore lint/complexity/noUselessFragments: We need to pass a trigger element to the AlertDialog so the AlertDialogProvider doesn't render the default trigger element
        triggerElement={<></>}
      />
    </>
  );
};
